import "react-popper-tooltip/dist/styles.css";
import * as PopperJS from "@popperjs/core";
import { usePopperTooltip } from "react-popper-tooltip";
import React from "react";
import ReactDOM from "react-dom";
import { cn } from "~/lib/utils";

// // // //

interface Props {
    placement?: PopperJS.Placement;
    interactive?: boolean;
    delayHide?: number;
    children: (childProps: {
        setTriggerRef:
            | React.Dispatch<React.SetStateAction<HTMLElement | null>>
            | undefined;
    }) => React.ReactNode;
    tooltipContent: React.ReactNode;
    tooltipArrowClassName?: string;
    tooltipWrapperClassName?: string;
}

/**
 * Generic, mostly-unstyled Tooltip component
 * Doc: https://github.com/mohsinulhaq/react-popper-tooltip
 */
export function Tooltip(props: Props) {
    const {
        placement,
        tooltipArrowClassName = "hidden",
        tooltipWrapperClassName = "bg-slate-800 text-white rounded-md border border-slate-700 shadow-lg",
        tooltipContent,
        interactive,
        delayHide,
        children,
    } = props;
    const {
        getArrowProps,
        getTooltipProps,
        setTooltipRef,
        setTriggerRef,
        visible,
    } = usePopperTooltip({ placement, interactive, delayHide });

    if (tooltipContent === null) {
        return children({ setTriggerRef: undefined });
    }

    // Defines the element rendered inside the tooltip
    const tooltipEl = (
        <div
            ref={setTooltipRef}
            {...getTooltipProps({
                className: cn("tooltip-container", tooltipWrapperClassName),
                style: {
                    "--tooltipBackground": "var(--background)",
                    "--tooltipBorder": "var(--border)",
                    padding: "0",
                } as React.CSSProperties,
            })}
        >
            <div
                {...getArrowProps({
                    className: cn(
                        "tooltip-arrow",
                        "border-border",
                        tooltipArrowClassName,
                    ),
                })}
            />
            {tooltipContent}
        </div>
    );

    return (
        <React.Fragment>
            <React.Fragment>{children({ setTriggerRef })}</React.Fragment>
            <TooltipPortal>
                <>{visible && tooltipEl}</>
            </TooltipPortal>
        </React.Fragment>
    );
}

function TooltipPortal(props: { children: React.ReactNode }) {
    const { children } = props;

    // Disable in SSR runtime
    if (typeof document === "undefined") {
        return null;
    }

    const portalNode = document.getElementById("tooltip-portal-container");
    if (!portalNode) {
        return null;
    }
    return ReactDOM.createPortal(children, portalNode);
}
